// src/pages/RolesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api/client";          // <-- use centralized axios with Authorization interceptor
import "../styles/RolesPage.css";

export default function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [form, setForm] = useState({ id: null, name: "", permission_ids: [] });
  const [viewRole, setViewRole] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const [r, p] = await Promise.all([
          api.get("/roles/"),
          api.get("/permissions/"),
        ]);
        setRoles(r.data || []);
        setPermissions(p.data || []);
      } catch (e) {
        console.error(e);
        setErr(e?.response?.data?.detail || "Failed to load roles/permissions");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const refreshRoles = async () => {
    const r = await api.get("/roles/");
    setRoles(r.data || []);
  };

  const handleCheckbox = (id) => {
    setForm((prev) => {
      const num = Number(id);
      const exists = prev.permission_ids.includes(num);
      return {
        ...prev,
        permission_ids: exists
          ? prev.permission_ids.filter((pid) => pid !== num)
          : [...prev.permission_ids, num],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");

    const payload = {
      name: form.name.trim(),
      permission_ids: form.permission_ids.map(Number),
    };

    try {
      if (form.id) {
        await api.put(`/roles/${form.id}`, payload);
      } else {
        await api.post("/roles/", payload);
      }
      await refreshRoles();
      resetForm();
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (role) => {
    setForm({
      id: role.id,
      name: role.name || "",
      permission_ids: (role.permissions || []).map((p) => Number(p.id)),
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure to delete this role?")) return;
    setErr("");
    try {
      await api.delete(`/roles/${id}`);
      await refreshRoles();
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.detail || "Delete failed");
    }
  };

  const handleView = (role) => setViewRole(role);

  const resetForm = () => setForm({ id: null, name: "", permission_ids: [] });

  const filteredRoles = useMemo(() => {
    const q = (search || "").toLowerCase();
    return roles.filter(
      (role) =>
        (role.name || "").toLowerCase().includes(q) ||
        (role.permissions || []).some((p) =>
          (p.name || "").toLowerCase().includes(q)
        )
    );
  }, [roles, search]);

  return (
    <div className="roles-page">
      <h2>{form.id ? "Edit Role" : "Create Role"}</h2>

      <form onSubmit={handleSubmit} className="form">
        <input
          name="name"
          placeholder="Role Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required className="form"
        />

        <table className="perm-table-checkbox">
          <thead>
            <tr>
              <th>Select</th>
              <th>Permission Name</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {permissions.map((perm) => {
              const pid = Number(perm.id);
              const checked = form.permission_ids.includes(pid);
              return (
                <tr key={perm.id}>
                  <td>
                    <input
                      type="checkbox"
                      value={pid}
                      onChange={() => handleCheckbox(pid)}
                      checked={checked}
                    />
                  </td>
                  <td>{perm.name}</td>
                  <td>{perm.description || "-"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <button type="submit" disabled={saving}>
          {saving ? "Saving..." : form.id ? "Update" : "Create"}
        </button>
        {form.id && (
          <button type="button" onClick={resetForm}>
            Cancel
          </button>
        )}
      </form>

      {err && <div className="error-banner">{err}</div>}
      {loading ? <div className="loading">Loading...</div> : null}

      <h3>Role List</h3>
      <input
        className="search-box"
        placeholder="Search roles..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <table className="role-table">
        <thead>
          <tr>
            <th>ID</th><th>Name</th><th>Permissions</th><th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredRoles.map((role) => (
            <tr key={role.id}>
              <td>{role.id}</td>
              <td>{role.name}</td>
              <td>{(role.permissions || []).map((p) => p.name).join(", ")}</td>
              <td>
                <button onClick={() => handleView(role)}>View</button>{" "}
                <button onClick={() => handleEdit(role)}>Edit</button>{" "}
                <button onClick={() => handleDelete(role.id)}>Delete</button>
              </td>
            </tr>
          ))}
          {filteredRoles.length === 0 && !loading && (
            <tr>
              <td colSpan="4" style={{ textAlign: "center" }}>
                No roles
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {viewRole && (
        <div className="modal" onClick={() => setViewRole(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>Role Details</h4>
              <button className="close-button" onClick={() => setViewRole(null)}>✖</button>
            </div>
            <table className="modal-table">
              <tbody>
                <tr><td><strong>ID</strong></td><td>{viewRole.id}</td></tr>
                <tr><td><strong>Name</strong></td><td>{viewRole.name}</td></tr>
                <tr><td><strong>Active</strong></td><td>{viewRole.is_active ? "Yes" : "No"}</td></tr>
                <tr>
                  <td><strong>Permissions</strong></td>
                  <td>
                    <ul style={{margin:0,paddingLeft:18}}>
                      {(viewRole.permissions || []).map((p) => (
                        <li key={p.id}>{p.name}</li>
                      ))}
                    </ul>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
